import { Form, type FormInstance } from 'antd';
import { type ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type FormValidationGate = {
  /** True while the form has validation errors -- hand it to the submit button. */
  isSubmitDisabled: boolean;
  /** Re-runs validation now, e.g. right after loading values into the form. */
  revalidate: () => void;
};

/**
 * Keeps a submit button disabled for exactly as long as antd says the form is invalid.
 *
 * Validation is re-run in full on every change, and only one round at a time counts:
 * antd keeps the latest `validateFields` call and rejects whatever that displaced with
 * `outOfDate: true`, carrying no error fields -- because the round was abandoned, not
 * because the form is clean. A rejection like that can settle *after* the round which
 * replaced it resolved, so a gate reading each outcome as it lands calls a form invalid
 * that never was, and the operator stays locked out until some later edit happens to win
 * the race. Hence the flag: it marks every displaced round, the ones antd starts by
 * itself included -- a single field's own trigger, a dependency update -- which nothing
 * here can count. The previous verdict is then left standing, the safe half of the trade,
 * since submitting revalidates and so a stale "enabled" cannot save an invalid form,
 * while a stale "disabled" would leave no way out at all.
 *
 * The run counter covers the other direction: a round still in flight when the form goes
 * off screen was displaced by nothing, so it settles normally and would move a button
 * that by then answers for whatever gets loaded next. `revalidate` refuses to start one
 * at all once the form is off screen, for the same reason.
 *
 * @param form   the form to validate
 * @param active whether the form is on screen; validation pauses while it is not
 */
const useFormValidationGate = (
  form: FormInstance,
  active = true
): FormValidationGate => {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const latestRun = useRef(0);

  // Read by `revalidate`, which a caller may hold on to and call from a callback of its
  // own -- by which time the `active` it closed over says nothing about now. Written on
  // commit, not during render: a render React starts and then throws away must not be
  // able to leave a "closed" behind that refuses to validate the form still on screen.
  const isActive = useRef(active);

  useLayoutEffect(() => {
    isActive.current = active;
  }, [active]);

  // Watching every field is what makes a change anywhere re-run validation.
  const values = Form.useWatch([], form);

  const revalidate = useCallback(() => {
    if (!isActive.current) {
      return;
    }

    const run = (latestRun.current += 1);

    const settle = (nextDisabled: boolean) => {
      if (latestRun.current === run) {
        setIsSubmitDisabled(nextDisabled);
      }
    };

    form.validateFields().then(
      () => settle(false),
      (reason: ValidateErrorEntity) => {
        if (!reason?.outOfDate) {
          settle(true);
        }
      }
    );
  }, [form]);

  useEffect(() => {
    // A verdict describes the form as it stood on screen. Once it is off screen, drop it
    // -- along with any round still in flight, which would only answer for that same
    // form -- so a reopened form starts locked rather than trusting the last one.
    if (!active) {
      latestRun.current += 1;
      setIsSubmitDisabled(true);

      return;
    }

    // Nothing to judge until the form has values.
    if (values === undefined) {
      return;
    }

    revalidate();
  }, [active, revalidate, values]);

  return { isSubmitDisabled, revalidate };
};

export default useFormValidationGate;
